import { Group } from '../group/group.model';
import { Team } from '../team/team.model';

export class UserMarkup {
    useInheritedValues: boolean = false;
    ratePerTrans: number = 0;
    costPerTrans: number = 0;
    fixedMonthlyFeeTotal: number = 0;
}

export class UserCommission {
    useInheritedValues: boolean = false;
    salesCommissionPercent: number = 0;
}

export enum RedirectPageEnum {
    LEAD = 'leads',
    MERCHANT = 'merchants',
    STATEMENT = 'statements',
    PROPOSAL = 'proposals',
    IMPERSONATE = 'impersonate'
}

export class User {
    constructor(user: Partial<User>) {
        Object.keys(this).forEach(key => {
            if (user[key] == null && this[key] == null)
                throw Error(`${key} cannot be null.`);
        });
        Object.assign(this, user);

        this.isAnonymous = false;
    }

    id: string | null = null;
    compatId: string | null = null;
    createdAt: Date | null = null;
    updatedAt: Date | null = null;
    lastSignInTime: Date| undefined;

    disabled: boolean | null = null;
    termsAccepted: boolean | null = null;
    isFromInvite: boolean | null = null;
    inviteCompleted: boolean | null = null;

    email: string | undefined;
    firstName: string | undefined;
    lastName: string | undefined;
    fullName: string | undefined; 
    phoneNumber: string | undefined;

    isAdmin: boolean | null = null;
    isAnonymous: boolean = true;
    hasReviewerAccess: boolean | null = null;
    isFreeTrialAccount: boolean | null = null;
    roleId: string | null = null;
    roleName: string | null = null;

    redirectPage: RedirectPageEnum | null = null;

    organizationId: string|undefined;

    groupId: string|undefined;
    teamId: string|undefined;

    defaultUserMarkup: UserMarkup | null = null;
    defaultUserCommission: UserCommission | null = null;

    // Post-Firebase compatibility functions
    // MD: These functions exist on the firebase.User object, but I don't know
    // if we still need a non-firebase version, so they're stubbed out right 
    // now.
    async updateProfile(profile: Partial<User>): Promise<void> {
        return Promise.resolve();
    }

    refreshUser(): Promise<void> {
        return Promise.resolve();
    }

    getIdToken(forceRefresh: boolean): Promise<string> {
        return Promise.resolve('');
    }

    // End Post-Firebase compatibility functions


    static getMarkup(u: User, g: Group, t: Team): UserMarkup {
        if (!u.defaultUserMarkup?.useInheritedValues) 
            return u.defaultUserMarkup as UserMarkup;

        switch (u.roleName) {
            case 'teamLead':
            case 'teamMember':
                if (!t.defaultUserMarkup.useInheritedValues) 
                    return t.defaultUserMarkup;
            case 'groupAdmin':
                if (!g.defaultUserMarkup.useInheritedValues) 
                    return g.defaultUserMarkup;
            case 'organizationAdmin':
            case 'organizationBillingAdmin':
            default:
                return u.defaultUserMarkup;
        }
    }

    static getCommissionPercent(u: User, g: Group, t: Team): number {
        if (!u.defaultUserCommission?.useInheritedValues) 
            return u.defaultUserCommission?.salesCommissionPercent ?? 0;

        switch (u.roleName) {
            case 'teamLead':
            case 'teamMember':
                if (!t.defaultUserCommission.useInheritedValues) 
                    return t.defaultUserCommission.salesCommissionPercent;
            case 'groupAdmin':
                if (!g.defaultUserCommission.useInheritedValues) 
                    return g.defaultUserCommission.salesCommissionPercent;
            case 'organizationAdmin':
            case 'organizationBillingAdmin':
                return u.defaultUserCommission?.salesCommissionPercent ?? 0;
            default:
                return 0;
        }
    }
}

export class UserPreview {
    id: string | null = null;
    compatId: string | null = null;
    organizationId: string|undefined;
    email: string | undefined;
    phoneNumber: number | undefined;
    fullName: string | undefined;
    roleName: string | null = null;
    roleId: string | null = null;
    organizationName: string|undefined;
    groupName: string|undefined;
    teamName: string|undefined;
    isAdmin: boolean | null = null;
    hasReviewerAccess: boolean | null = null;
    disabled: boolean | null = null;

    // These are required by organization-users.component.ts
    isFromInvite: boolean | null = null;
    inviteCompleted: boolean | null = null;

    lastSignInTime: Date | string = 'Never';
    createdAt: Date | null = null;

    constructor(user: Partial<UserPreview>) {
        Object.keys(this).forEach(key => {
            if (user[key] == null && this[key] == null)
                throw Error(`${key} cannot be null.`);
        });
        Object.assign(this, user);
    }
}
